import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tabs,
} from "antd";
import UserService from "../../services/UserService";
import RoleService from "../../services/RoleService";
import UserGroupService from "../../services/UserGroupService";
import UserPermissionService from "../../services/UserPermissionService";
import { IRoleResponseData } from "../../types/Role";
import { IUserGroupResponseData } from "../../types/UserGroup";
import { LoginMethod } from "../../types/User";
import { generatePassword, openNotification } from "../../utils";

interface UserEditModalProps {
  open: boolean;
  onClose: () => void;
  userId: number | null;
  userData: any;
  onSuccess: () => void;
}

const passwordRules = [
  { required: true, message: "Şifre zorunludur" },
  { min: 12, message: "En az 12 karakter" },
];

const UserEditModal: React.FC<UserEditModalProps> = ({
  open,
  onClose,
  userId,
  userData,
  onSuccess,
}) => {
  const isAdmin = localStorage.getItem("roleName") === "ADMIN";
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [settingsForm] = Form.useForm();
  const [roleForm] = Form.useForm();
  const [groupForm] = Form.useForm();
  const [roles, setRoles] = useState<IRoleResponseData[]>([]);
  const [groups, setGroups] = useState<IUserGroupResponseData[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("LOCAL");

  useEffect(() => {
    if (!open || !userData) return;

    const userLoginMethod = userData.loginMethod || "LOCAL";
    setLoginMethod(userLoginMethod);
    profileForm.setFieldsValue({
      username: userData.username,
      firstName: userData.firstName,
      lastName: userData.lastName,
      gsmPortalEmployeeId: userData.gsmPortalEmployeeId,
      phoneNumber: userData.phoneNumber,
      loginMethod: userLoginMethod,
    });
    settingsForm.setFieldsValue({
      salesTarget: userData.salesTarget,
      active: userData.active,
      autoAssignment: userData.autoAssignment,
    });
    setSelectedRoles((userData.roles || []).map((role: any) => role.id));

    if (userData.permissions?.length > 0) {
      const groupId = userData.permissions[0].userGroupId;
      setSelectedGroup(groupId);
      groupForm.setFieldsValue({ userGroupId: groupId });
    } else {
      setSelectedGroup(undefined);
      groupForm.resetFields();
    }
  }, [open, userData, profileForm, settingsForm, groupForm]);

  useEffect(() => {
    if (open && isAdmin) {
      RoleService.getAll().then((res) => setRoles(res.data)).catch(console.log);
      UserGroupService.getAll().then((res) => setGroups(res.data)).catch(console.log);
    }
  }, [open, isAdmin]);

  const saveProfile = async (values: any) => {
    if (!userId) return;
    setLoading(true);
    try {
      await UserService.updateById(userId, values);
      openNotification("success", "Başarılı", "Profil güncellendi");
      onSuccess();
    } catch (error: any) {
      openNotification("error", "Hata", error?.response?.data?.message || "Profil güncellenemedi");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (values: any) => {
    if (!userId) return;
    setLoading(true);
    try {
      await UserService.updateById(userId, values);
      if (values.active === false) {
        await UserService.softDelete(userId);
      } else if (typeof values.active === "boolean") {
        await UserService.updateStatusUserById({ userId, active: values.active });
      }
      if (typeof values.autoAssignment === "boolean") {
        await UserService.updateAutoAssignmentUserById({
          userId,
          autoAssignment: values.autoAssignment,
        });
      }
      if (values.salesTarget != null) {
        await UserService.updateSalesTargetByUserId(userId, {
          salesTarget: values.salesTarget,
        });
      }
      openNotification("success", "Başarılı", "Ayarlar güncellendi");
      onSuccess();
    } catch (error: any) {
      openNotification("error", "Hata", error?.response?.data?.message || "Ayarlar güncellenemedi");
    } finally {
      setLoading(false);
    }
  };

  const saveRoles = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      await UserService.updateRoleByUserId(userId, { roleIdList: selectedRoles });
      openNotification("success", "Başarılı", "Roller güncellendi");
      onSuccess();
    } catch (error) {
      openNotification("error", "Hata", "Roller güncellenemedi");
    } finally {
      setLoading(false);
    }
  };

  const saveGroup = async () => {
    if (!userId || !selectedGroup) return;
    setLoading(true);
    const payload = { userGroupId: selectedGroup, userId };
    try {
      if (userData.permissions?.length > 0) {
        await UserPermissionService.update(
          userId,
          userData.permissions[0].userGroupId,
          payload
        );
      } else {
        await UserPermissionService.create(payload);
      }
      openNotification("success", "Başarılı", "Grup güncellendi");
      onSuccess();
    } catch (error) {
      openNotification("error", "Hata", "Grup güncellenemedi");
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (values: any) => {
    if (!userId) return;
    setLoading(true);
    try {
      await UserService.resetPassword(userId, { newPassword: values.newPassword });
      openNotification("success", "Başarılı", "Şifre sıfırlandı");
      passwordForm.resetFields();
    } catch (error: any) {
      openNotification("error", "Hata", error?.response?.data?.message || "Şifre sıfırlanamadı");
    } finally {
      setLoading(false);
    }
  };

  const tabItems = [
    {
      key: "profile",
      label: "Profil",
      children: (
        <Form form={profileForm} layout="vertical" onFinish={saveProfile}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="username" label="Kullanıcı adı" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phoneNumber" label="Telefon">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="firstName" label="Ad" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lastName" label="Soyad" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="gsmPortalEmployeeId" label="GSM Portal Employee ID">
                <Input />
              </Form.Item>
            </Col>
            {isAdmin && (
              <>
                <Col span={24}>
                  <Form.Item name="loginMethod" label="Giriş yöntemi">
                    <Select
                      onChange={(value: LoginMethod) => setLoginMethod(value)}
                      options={[
                        { label: "Şifre ile (Local)", value: "LOCAL" },
                        { label: "Google ile", value: "GOOGLE" },
                        { label: "Gate ile", value: "GATE" },
                      ]}
                    />
                  </Form.Item>
                </Col>
                {loginMethod === "LOCAL" &&
                  userData?.loginMethod !== "LOCAL" && (
                  <Col span={24}>
                    <Form.Item
                      name="password"
                      label="Yeni şifre"
                      rules={[
                        { required: true, message: "Local girişe geçiş için şifre zorunludur" },
                        { min: 12, message: "En az 12 karakter" },
                      ]}
                    >
                      <Input.Password placeholder="Şifre" />
                    </Form.Item>
                  </Col>
                )}
              </>
            )}
          </Row>
          <Button type="primary" htmlType="submit" loading={loading}>
            Profili Kaydet
          </Button>
        </Form>
      ),
    },
    ...(isAdmin
      ? [
          {
            key: "roles",
            label: "Roller & Grup",
            children: (
              <Space direction="vertical" style={{ width: "100%" }} size="large">
                <div>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Roller</div>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    placeholder="Rol seçin"
                    value={selectedRoles}
                    onChange={setSelectedRoles}
                    options={roles.map((role) => ({
                      label: role.description,
                      value: role.id,
                    }))}
                  />
                  <Button
                    type="primary"
                    style={{ marginTop: 12 }}
                    onClick={saveRoles}
                    loading={loading}
                  >
                    Rolleri Kaydet
                  </Button>
                </div>
                <div>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Grup</div>
                  <Form form={groupForm} layout="vertical">
                    <Form.Item name="userGroupId">
                      <Select
                        placeholder="Grup seçin"
                        style={{ width: "100%" }}
                        onChange={setSelectedGroup}
                        options={groups.map((group) => ({
                          label: group.groupName,
                          value: group.id,
                        }))}
                      />
                    </Form.Item>
                  </Form>
                  <Button type="primary" onClick={saveGroup} loading={loading}>
                    Grubu Kaydet
                  </Button>
                </div>
              </Space>
            ),
          },
          ...(userData?.loginMethod === "LOCAL"
            ? [
                {
                  key: "security",
                  label: "Güvenlik",
                  children: (
                    <Form form={passwordForm} layout="vertical" onFinish={resetPassword}>
                      <Form.Item name="newPassword" label="Yeni şifre" rules={passwordRules}>
                        <Input.Password />
                      </Form.Item>
                      <Space>
                        <Button
                          onClick={() =>
                            passwordForm.setFieldsValue({ newPassword: generatePassword(14) })
                          }
                        >
                          Rastgele Şifre
                        </Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                          Şifreyi Sıfırla
                        </Button>
                      </Space>
                    </Form>
                  ),
                },
              ]
            : []),
        ]
      : []),
    {
      key: "settings",
      label: "Ayarlar",
      children: (
        <Form form={settingsForm} layout="vertical" onFinish={saveSettings}>
          <Form.Item name="salesTarget" label="Sales Target">
            <Input type="number" />
          </Form.Item>
          {isAdmin && (
            <>
              <Form.Item name="active" label="Aktif" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="autoAssignment" label="Otomatik Atama" valuePropName="checked">
                <Switch />
              </Form.Item>
            </>
          )}
          <Button type="primary" htmlType="submit" loading={loading}>
            Ayarları Kaydet
          </Button>
        </Form>
      ),
    },
  ];

  return (
    <Modal
      title={userData ? `${userData.firstName} ${userData.lastName}` : "Kullanıcı"}
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      <Tabs items={tabItems} />
    </Modal>
  );
};

export default UserEditModal;
